import {useState,type FormEvent} from 'react';

type Category='BUG'|'QUESTION'|'SUGGESTION';
const categoryLabels:Record<Category,string>={
  BUG:'Report a Problem',QUESTION:'Ask a Question',SUGGESTION:'Make a Suggestion',
};
const urgencyOptions=[
  'Minor issue / I can still work',
  'This slows me down',
  'This prevents me from doing my job.',
] as const;

/** FloatHelm/v79 Help & Feedback parity, no Subject field for any type.
 * Problem: Urgency, What Happened, Expected Outcome, Steps to Reproduce.
 * Submissions remain source-backed internal App Support tasks, not outbound.
 */
export function HelpFeedbackModal({locationCode,onClose}:{
  locationCode:string;onClose:()=>void;
}){
  const [category,setCategory]=useState<Category>('BUG');
  const [urgency,setUrgency]=useState<string>(urgencyOptions[0]);
  const [whatHappened,setWhatHappened]=useState('');
  const [expectedOutcome,setExpectedOutcome]=useState('');
  const [stepsToReproduce,setStepsToReproduce]=useState('');
  const [description,setDescription]=useState('');
  const [requestKey,setRequestKey]=useState(()=>crypto.randomUUID());
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const [saved,setSaved]=useState<{taskId:string}|null>(null);
  const edit=()=>{setRequestKey(crypto.randomUUID());setError('');};
  const valid=category==='BUG'?
    Boolean(whatHappened.trim()&&expectedOutcome.trim()&&stepsToReproduce.trim()):
    Boolean(description.trim());
  const submit=async(event:FormEvent)=>{
    event.preventDefault();
    if(busy||!valid)return;
    setBusy(true);setError('');
    try{
      const feedback=category==='BUG'?{
        category,urgency,whatHappened:whatHappened.trim(),
        expectedOutcome:expectedOutcome.trim(),
        stepsToReproduce:stepsToReproduce.trim(),
      }:{category,description:description.trim()};
      const response=await fetch('/api/v1/staff/support/feedback',{
        method:'POST',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,...feedback,
          idempotencyKey:requestKey}),
      });
      const body=await response.json() as {ok:boolean;data?:{taskId:string};
        error?:{code:string}};
      if(!response.ok||!body.ok||!body.data?.taskId)
        throw Error(body.error?.code??'Feedback could not be saved.');
      setSaved(body.data);
      window.dispatchEvent(new Event('ecfs:feedback-submitted'));
    }catch(e){
      setError(e instanceof Error?e.message:'Feedback could not be saved.');
    }finally{setBusy(false);}
  };
  return <div className="ecfs-help-feedback-backdrop" onMouseDown={event=>{
    if(event.target===event.currentTarget&&!busy)onClose();
  }}>
    <section role="dialog" aria-modal="true" aria-label="Help & Feedback"
      className="ecfs-help-feedback-dialog">
      <header><h2>Help &amp; Feedback</h2>
        <button type="button" aria-label="Close Help & Feedback"
          disabled={busy} onClick={onClose}>×</button>
      </header>
      {saved?<div role="status">
        <p>Submitted for follow-up in App Support / East Coast Float Spa Support.</p>
        <button type="button" onClick={onClose}>Close</button>
      </div>:<form onSubmit={event=>void submit(event)}>
        <label>What can we help with?
          <select aria-label="Help category" value={category} onChange={e=>{
            setCategory(e.target.value as Category);edit();
          }}>
            {Object.entries(categoryLabels).map(([id,title])=>
              <option key={id} value={id}>{title}</option>)}
          </select>
        </label>
        {category==='BUG'?<>
          <label>Urgency
            <select aria-label="Urgency" value={urgency} onChange={e=>{
              setUrgency(e.target.value);edit();
            }}>
              {urgencyOptions.map(option=><option key={option} value={option}>
                {option}</option>)}
            </select>
          </label>
          <label>What Happened
            <textarea aria-label="What Happened" rows={3} maxLength={6000}
              placeholder="A concise description of the problem."
              required value={whatHappened} onChange={e=>{
                setWhatHappened(e.target.value);edit();
              }}/>
          </label>
          <label>Expected Outcome
            <textarea aria-label="Expected Outcome" rows={3} maxLength={6000}
              placeholder="What did you expect to happen?"
              required value={expectedOutcome} onChange={e=>{
                setExpectedOutcome(e.target.value);edit();
              }}/>
          </label>
          <label>Steps to Reproduce
            <textarea aria-label="Steps to Reproduce" rows={3} maxLength={6000}
              placeholder="Please include any helpful steps or details needed to reproduce the problem."
              required value={stepsToReproduce} onChange={e=>{
                setStepsToReproduce(e.target.value);edit();
              }}/>
          </label>
        </>:<label>{category==='QUESTION'?'Question':'Feature Idea'}
          <textarea aria-label={category==='QUESTION'?'Question':'Feature Idea'}
            placeholder={category==='QUESTION'?
              'What would you like help understanding?':
              'Describe the feature or improvement you would like to see.'}
            maxLength={6000} rows={6} required
            value={description} onChange={e=>{
              setDescription(e.target.value);edit();
            }}/>
        </label>}
        <p>Submissions create a task for authorized App Support staff to follow up.</p>
        {error&&<p role="alert">{error}</p>}
        <footer><button type="button" disabled={busy}
            onClick={onClose}>Cancel</button>
          <button type="submit" disabled={busy||!valid}>
            {busy?'Submitting…':'Submit'}</button>
        </footer>
      </form>}
    </section>
  </div>;
}
